import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe, faCaretUp, faClock } from "@fortawesome/free-solid-svg-icons";
import { useAppData } from "../context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateUser } from "../api/usersApi";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import TimezoneSelect from "react-timezone-select";

const Footer = () => {
  const { i18n } = useTranslation();
  const data = useAppData();
  const timeout = parseInt(window.appConfig?.universalTimeout);
  const currentLang = data?.user?.language || "en";
  const languageOptions = window.appConfig?.languageCodes || [];

  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  const queryClient = useQueryClient();

  const langMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['customer_blob']);
    },
    onError: (err) => {
      console.error('Could not save language:', err);
    },
  });

  const currentLangLabel =
    languageOptions.find((opt) => opt.value === currentLang)?.label ||
    currentLang;

  const onLanguageSelect = (value) => {
    if (!data?.user?.id || !data?.customer?.id) return;
    if (value !== currentLang) {
      langMutation.mutate({
        queryKey: [
          'language-preference',
          data.user.id,
          {
            customer_id: data.customer.id,
            language: value,
          },
          timeout,
        ],
      });
    }
  };

  const timezoneMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries(["customer_blob"]);
    },
    onError: (err) => {
      console.error("Could not save timezone:", err);
    },
  });

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Try to get timezone from blob → localStorage → browser
  const initialTz =
    data?.user?.timezone ||
    localStorage.getItem("user-timezone") ||
    browserTz;

  const [selectedTimezone, setSelectedTimezone] = useState(initialTz);

  return (
    <footer className="bg-light p-3">
      <div className="footer-main">
        <div className="footer-pickers-row">
          <div className="footer-picker d-flex align-items-center">
            <UncontrolledDropdown direction="up">
              <DropdownToggle nav className="dropdown d-flex align-items-center">
                <FontAwesomeIcon icon={faGlobe} className="mr-1" />
                {currentLangLabel}
                <FontAwesomeIcon
                  icon={faCaretUp}
                  style={{ marginLeft: "0.25rem", fontSize: "1.0em" }}
                  aria-label="Dropdown chevron"
                />
              </DropdownToggle>
              <DropdownMenu end className="dropdown-menu">
                {languageOptions.map(({ value, label }) => (
                  <DropdownItem
                    key={value}
                    active={value === currentLang}
                    onClick={() => onLanguageSelect(value)}
                  >
                    {label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
          <div className="footer-picker d-flex align-items-center">
            <FontAwesomeIcon icon={faClock} color="var(--brand-navy)" className="mr-1" />
            <div style={{ maxWidth: 240 }}>
              <TimezoneSelect
                value={selectedTimezone}
                menuPlacement="auto"
                isSearchable={false}
                styles={{
                  control: (base) => ({
                    ...base,
                    background: "none",
                    border: "none",
                    boxShadow: "none",
                    minHeight: "unset",
                    padding: 0,
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "var(--brand-navy)",
                    fontSize: "1rem",
                    fontWeight: 500,
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: "var(--brand-navy)",
                    paddingLeft: "0.25rem",
                    paddingRight: "0.25rem",
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    paddingLeft: 0,
                    paddingRight: 0,
                  }),
                }}
                components={{
                  DropdownIndicator: (props) => (
                    <FontAwesomeIcon
                      icon={faCaretUp}
                      color="var(--brand-navy)"
                      style={{ marginLeft: "0.25rem", fontSize: "1.0em" }}
                      aria-label="Timezone dropdown chevron"
                      {...props.innerProps}
                    />
                  ),
                }}
                onChange={(tz) => {
                  const newTimezone = tz.value;
                  setSelectedTimezone(newTimezone);
                  localStorage.setItem("user-timezone", newTimezone);
                  timezoneMutation.mutate({
                    queryKey: [
                      "timezone-preference",
                      data.user.id,
                      {
                        customer_id: data.customer.id,
                        timezone: tz.value,
                      },
                      timeout,
                    ],
                  });
                }}
              />
            </div>
          </div>
        </div>
        <div className="footer-copyright-row">
          <p className="text-muted mb-0">
            <small>ReVolta &copy;2025 v{window.appConfig.version}</small>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
